import { Link } from 'react-router-dom'
import {
  ArrowLeft,
  Trash2,
  Activity,
  Loader2,
  Clock,
  MessageCircle,
  RefreshCw,
} from 'lucide-react'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { useSROSessions, useSROSessionStats, useDeleteSROSession } from '@/hooks/useSRO'
import { useToast } from '@/hooks/use-toast'
import { useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'

export function SROSessions() {
  const { data: sessionData, isLoading } = useSROSessions()
  const { data: stats } = useSROSessionStats()
  const deleteSession = useDeleteSROSession()
  const { toast } = useToast()
  const qc = useQueryClient()
  const [deleteId, setDeleteId] = useState<string | null>(null)

  const sessions = sessionData?.sessions || []

  const handleDelete = () => {
    if (!deleteId) return
    deleteSession.mutate(deleteId, {
      onSuccess: () => { toast({ title: 'Session deleted' }); setDeleteId(null) },
      onError: () => toast({ title: 'Delete failed', variant: 'destructive' }),
    })
  }

  const statusColors: Record<string, string> = {
    in_progress: 'bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300',
    completed: 'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300',
    abandoned: 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300',
  }

  if (isLoading) {
    return <div className="flex items-center justify-center h-64"><Loader2 className="h-8 w-8 animate-spin text-muted-foreground" /></div>
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Link to="/app/sro"><Button variant="ghost" size="icon"><ArrowLeft className="h-5 w-5" /></Button></Link>
          <div>
            <h1 className="text-2xl font-bold">Active Sessions</h1>
            <p className="text-muted-foreground">Monitor and manage active conversations</p>
          </div>
        </div>
        <Button variant="outline" onClick={() => {
          qc.invalidateQueries({ queryKey: ['sro-sessions'] })
          qc.invalidateQueries({ queryKey: ['sro-session-stats'] })
        }}>
          <RefreshCw className="h-4 w-4 mr-2" />Refresh
        </Button>
      </div>

      {/* Stats */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{stats.total_active}</div>
              <p className="text-xs text-muted-foreground">Total Active</p>
            </CardContent>
          </Card>
          {Object.entries(stats.by_workflow || {}).map(([workflow, count]) => (
            <Card key={workflow}>
              <CardContent className="pt-6">
                <div className="text-2xl font-bold">{count as number}</div>
                <p className="text-xs text-muted-foreground capitalize">{(workflow as string).replace('_', ' ')}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Sessions List */}
      <div className="space-y-3">
        {sessions.map((session: any) => (
          <Card key={session.session_id}>
            <CardContent className="pt-6">
              <div className="flex items-start justify-between">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  <div className="rounded-lg bg-primary/10 p-2">
                    <MessageCircle className="h-5 w-5 text-primary" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-mono text-sm truncate max-w-[200px]">{session.session_id}</span>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${statusColors[session.status] || ''}`}>
                        {session.status}
                      </span>
                      {session.workflow && (
                        <Badge variant="outline" className="text-xs capitalize">
                          {session.workflow.replace('_', ' ')}
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Activity className="h-3 w-3" />Step {session.step}</span>
                      <span className="flex items-center gap-1"><MessageCircle className="h-3 w-3" />{session.history_length} messages</span>
                      <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{new Date(session.created_at).toLocaleString()}</span>
                    </div>
                    {session.data && Object.keys(session.data).length > 0 && (
                      <div className="mt-2 flex flex-wrap gap-1">
                        {Object.entries(session.data).slice(0, 5).map(([key, val]) => (
                          <span key={key} className="text-xs bg-muted px-2 py-0.5 rounded">
                            {key}: {String(val).substring(0, 30)}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <Button variant="ghost" size="icon" onClick={() => setDeleteId(session.session_id)}>
                  <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}

        {sessions.length === 0 && (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12 gap-3">
              <Activity className="h-10 w-10 text-muted-foreground" />
              <p className="text-muted-foreground">No active sessions</p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Session?</AlertDialogTitle>
            <AlertDialogDescription>This will terminate the conversation and remove the session data.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
